<template>
  <div :class="style()" class="payment-chooser full-width">
    <div class="full-width">
      <!-- Chosen Payment Method -->
      <div v-if="hasPaymentAccounts">
        <!-- each payment choice -->
        <div class="full-width" :key="index" v-for="(payment, index) in getPaymentAccounts()">
          <CheckoutChoice
            v-if="displayPaymentAccount(payment)"
            :name="paymentName(payment)"
            :isActive="isActivePayment(payment)"
            :clickHandler="() => {setPayment(payment)}"
          >
            <a class="align-right"
              href="/?delete-payment"
              @click.prevent="deletePaymentMethod(payment)">Delete</a>
          </CheckoutChoice>
        </div>
      </div>
    </div>
    <div class="full-width section-actions">
        <a class="link-action" v-if="hasMultiplePaymentAccounts"
            href="/?show-payments"
            @click.prevent="showAllPayments = !showAllPayments">
            {{(!showAllPayments) ? "Show" : "Hide"}} all
        </a>

      <a class="link-action" v-if="isCustomerOrGuest"
        href="/?add-payment"
        @click.prevent="triggerShowPayment">
            {{showAddPayment ? 'Hide' : 'Add new'}}
        </a>
    </div>

	<div v-if="showPayment" style="width:100%;" class="resp-iframe-container">
		<p class="store-navigation-color">Add your payment method</p>
		<payment-iframe
			:iFrameProps="addPaymentInfo"
			@dateChosen="dateChosen"
			@iFrameEvent="handleIFrameResponse"
			@addPayment="addPayment"
      @captchaSolved="captchaSolved"
		/>
	</div>

    <div>
      <p>{{msg}}</p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { ACCOUNT, ACCOUNT_CHECKOUT, LOCATION } from '@/stores/defs';
import { EPaymentServiceType } from '../../models/interfaces/Payment/EPaymentServiceType';
import { PAYMENT_CMDS } from '../../common/api-cmds/paymentCmds';
import { getParam } from '@/utils/helper.functions';
import { ERROR_MSG } from '@/common/messages/error.msg';
import { SUCCESS_MSG } from '@/common/messages/success.msg';
import { PAYMENT_ANALYTIC_EVENT } from "@/common/analytic-events/paymentAnalyticEvent";
import EventBus from '@EventBus';
import Analytics from "@Analytics";
import FormValidator from "@/utils/FormValidator";
import PaymentAccountModel from '@/models/PaymentAccountModel';
import CheckoutChoice from '@/components/checkout/CheckoutChoice';
import paymentService, { PAYMENT_ENDPOINTS } from '@/services/payment.service';
import HorizontalInput from "@/components/form/HorizontalInput";
import paymentMixin from '@Mixin/paymentMixin';
import CreditCardExpiration from './payment/CreditCardExpiration';
import PaymentIFrame from './payment/PaymentIFrame';

export default {
    components: {
        CheckoutChoice,
        'horizontal-input': HorizontalInput,
        'credit-card-expiration': CreditCardExpiration,
        'payment-iframe': PaymentIFrame,
	},
	mixins: [paymentMixin],
    computed: {
        ...mapGetters({
			customer: ACCOUNT.GET_CUSTOMER,
			isGuest: ACCOUNT_CHECKOUT.GET_IS_GUEST,
			guestId: ACCOUNT_CHECKOUT.GET_GUEST_ID,
            location: LOCATION.GET_LOCATION,
            paymentMethod: ACCOUNT_CHECKOUT.GET_PAYMENT_METHOD
		}),
		hasPaymentAccounts() {
			const customer = this.customer;
			if (!customer.paymentAccounts) {
				return false;
			}
			if (customer.paymentAccounts.length > 0) {
				return true;
			}
			return false;
		},
		hasMultiplePaymentAccounts() {
			if (!this.hasPaymentAccounts) {
				return;
			}
			const customer = this.customer;
			const paymentAccounts = customer.paymentAccounts;
			return (paymentAccounts.length > 1);
		},
		isCustomerOrGuest() {
			const customer = this.customer;
			const isGuest = this.isGuest;
			if (customer.id || isGuest) {
				return true;
			}
			return false;
        },
        showPayment() {
            if (this.showAddPayment) {
                if (this.location.paymentServiceType === EPaymentServiceType.TYPE_CC) {
                    return true;
                } else if (this.location.paymentServiceType === EPaymentServiceType.TYPE_E) {
                    if (this.addPaymentInfo) {
                        return true;
                    }
                }
            }
            return false;
        },
    },
    data() {
        return {
            addPaymentInfo: null,
            showAddPayment: false,
            msg: "",
            chosenPaymentKey: null,
            showAllPayments: false,
            token: null,
            iframeUrl: null,
            expiry: null,
            solvedCaptcha: null
        }
    },
    methods: {
        ...mapActions({
            setPaymentMethod: ACCOUNT_CHECKOUT.SET_PAYMENT_METHOD,
            addPaymentAccount: ACCOUNT.ADD_PAYMENT,
            deletePayment: ACCOUNT.DELETE_PAYMENT
		}),
		style() {
            return {
                'is-preorder': !this.isPayNow,
			};
        },
        dateChosen(expiry) {
            this.expiry = expiry;
        },
        triggerShowPayment() {
            this.showAddPayment = !this.showAddPayment
            this.setupPayment();
        },
		displayPaymentAccount(payment) {
			const showAll = this.showAllPayments;
			if (showAll) {
				return true;
			}
			const paymentMethod = this.paymentMethod;
			const hasAccount = (paymentMethod && payment.id === paymentMethod.id);
			return hasAccount;
		},
		isActivePayment(payment) {
			return (this.paymentMethod && payment.id === this.paymentMethod.id);
		},
		paymentName(payment) {
			const active = this.isActivePayment(payment);
			const name = this._getPaymentNameWithMask(payment, active);
			return name;
		},
		getCustomerId() {
			return this.customer.id || this.guestId;
		},
		getPaymentAccounts() {
			const paymentAccounts = this.customer.paymentAccounts;
			return paymentAccounts;
        },
        // start, delete, finish
        getBaseApiConfig(cmd='start') {
            const baseConfig = {
                b: this.getCustomerId(),
                lb: this.location.id
            }
            const commonCmds = ['delete', 'get'];

            if (commonCmds.includes(cmd)) {
                switch (cmd) {
                    case 'delete':
                        baseConfig.c = PAYMENT_CMDS.COMMON.DELETE;
                        break;
                    case 'get':
                        baseConfig.c = PAYMENT_CMDS.COMMON.GET;
                        break;
                }
            } else {
                switch (this.location.paymentServiceType) {
                    case EPaymentServiceType.TYPE_E:
                        switch (cmd) {
                            case 'start':
                                    baseConfig.c = PAYMENT_CMDS.TYPE_E.START;
                                    baseConfig.ru = window.location.protocol + "//" + window.location.host + '/payment-response'
                                break;
                            case 'finish':
                                    baseConfig.c = PAYMENT_CMDS.TYPE_E.FINISH;
                                break;
                            case 'delete':
                                baseConfig.c = PAYMENT_CMDS.COMMON.DELETE;
                                break;
                        }
                        break;
                    case EPaymentServiceType.TYPE_CC:
                        switch (cmd) {
                            case 'finish':
                                baseConfig.c = PAYMENT_CMDS.TYPE_CC.START
                                // we get token when credit card is filled out in iFrame & event listener sets it
                                baseConfig.t = JSON.stringify({
                                    token: this.token,
                                    expiry: this.expiry
                                });
                                break;
                            case 'delete':
                                baseConfig.c = PAYMENT_CMDS.COMMON.DELETE;
                                break;
                        }
                }
            }

            return baseConfig;
        },
       setupPayment() {
            const customer = this.customer;
            if (!customer.paymentAccounts) {
                customer.paymentAccounts = [];
            }
            const customerId = this.getCustomerId();

            // either need to get token from 3rd party or get url from our own server
            switch (this.location.paymentServiceType) {
                case EPaymentServiceType.TYPE_E:
                    // get HPP URL
                    const apiConfig = this.getBaseApiConfig('start');

                    if (customerId && this.location.id) {
                        paymentService
                            .addPaymentMethod(apiConfig)
                            .then(res => {
                                if (res.data.message === SUCCESS_MSG.ORDER.PAYMENT.FLOW_BEGIN &&
                                    res.data.body.transactionSetupID &&
                                    res.data.body.hppUrl) {

                                    this.addPaymentInfo = {
                                        account_id: res.data.body.account_id,
                                        hppUrl: res.data.body.hppUrl
                                    };
                                } else if (res.data.body.hasOwnProperty('errors')) {
                                    this.msg = res.data.message;
                                } else {
                                    this.msg = ERROR_MSG.ORDER.PAYMENT.CREATION_ERR;
                                }
                            })
                            .catch(err => {
                                this.msg = err.message;
                            });
                    }
                    break;
            }
        },
        createPaymentAccount(account) {
            const paymentAccountModel = new PaymentAccountModel(account)
            this.addPaymentAccount(paymentAccountModel);
            this.setPayment(paymentAccountModel);
            this.$tpa.trackUserPayment(paymentAccountModel, 'Add');
            this.showAddPayment = false;
        },
        addPayment(apiConfig=null) {
            if(this.solvedCaptcha === null) {
              console.error('Captcha not completed');
              return;
            }
            switch (this.location.paymentServiceType) {
                case EPaymentServiceType.TYPE_E:
                    apiConfig.captcha = this.solvedCaptcha;
                    paymentService.addPaymentMethod(apiConfig)
                        .then((res) => {
                            if (res.data.message === SUCCESS_MSG.ORDER.PAYMENT.CREATED) {
                                this.msg = '';
                                this.addPaymentInfo = null;
                                this.createPaymentAccount({...res.data.body.account, id: res.data.body.account.account_id})
                            } else {
                                this.msg = ERROR_MSG.ORDER.PAYMENT.CREATION_ERR;
                            }
                        })
                        .catch((err) => {
                            this.msg = ERROR_MSG.ORDER.PAYMENT.CREATION_ERR;
                        });
                    break;
                case EPaymentServiceType.TYPE_CC:
                    if (this.token && this.expiry) {

                        const addApiConfig = this.getBaseApiConfig('finish');
                        addApiConfig.captcha = this.solvedCaptcha;
                         paymentService.addPaymentMethod(addApiConfig)
                            .then((res) => {
                                if (res.data.message === SUCCESS_MSG.ORDER.PAYMENT.ADDED && res.data.body.account) {
                                    this.msg = '';
                                    this.createPaymentAccount({...res.data.body.account, id: res.data.body.account.account_id});
                                } else {
                                    this.msg = ERROR_MSG.ORDER.PAYMENT.CREATION_ERR;
                                }
                            })
                            .catch((err) => {
                                this.msg = ERROR_MSG.ORDER.PAYMENT.CREATION_ERR;
                            })
                    } else {
                        if (!this.token) {
                            this.msg = ERROR_MSG.ORDER.PAYMENT.INVALID_CARD_NUMBER;
                        }

                        if (!this.expiry) {
                            this.msg = ERROR_MSG.ORDER.PAYMENT.INVALID_EXPIRATION;
                        }
                    }
                    break;
            }
		},
		setPayment(payment) {
			this.setPaymentMethod(payment);
			this.$emit('selectPayment', payment);
			this.$tpa.trackUserPayment(payment, 'Set');
		},
        deletePaymentMethod(paymentMethod) {
			this.msg = "";
			const customerId = this.getCustomerId();
            this.$modal.show("dialog", {
				text: "Are you sure you want to delete this payment method?",
				buttons: [
					{
						title: "Yes",
                        handler: () => {
                        let baseApiConfig = this.getBaseApiConfig('delete');
                        baseApiConfig.a = paymentMethod.id;

                        paymentService.deletePaymentMethod(baseApiConfig)
                            .then((res) => {
                                if (res.data.message === SUCCESS_MSG.ORDER.PAYMENT.API_DELETION_MSG) {
									this.$tpa.trackUserPayment(paymentMethod, 'Remove');
									this.msg = SUCCESS_MSG.ORDER.PAYMENT.DELETED
                                    this.deletePayment(paymentMethod.id);
                                    if (this.customer.paymentAccounts && this.customer.paymentAccounts.length) {
                                        this.setPayment(this.customer.paymentAccounts[0]);
                                    }

                                    setTimeout(() => {
                                        this.msg = "";
                                    }, 3000);


                                } else {
                                    this.msg = ERROR_MSG.ORDER.PAYMENT.DELETION_ERR;
                                }

                                this.$modal.hide("dialog");
                            })
                            .catch((err) => {
                                this.msg = ERROR_MSG.ORDER.PAYMENT.DELETION_ERR;

                                this.$modal.hide("dialog");
                            });

                         }
                    },
                    {
						title: "No",
					},
				],
            });
		},
        handleIFrameResponse(response) {
            if (this.location.paymentServiceType === EPaymentServiceType.TYPE_E) {
                try {
                    if (response.status === "Cancelled") {
                        this.showAddPayment = false;
                        this.addPaymentInfo = null;
                    } else if (response.query && response.status === "Complete") {
                        this.showAddPayment = false;
                        let apiConfig = this.getBaseApiConfig('finish');
                        apiConfig.t = JSON.stringify({
                                account_id: this.addPaymentInfo.account_id,
                                query: response.query
                        });
                        this.addPayment(apiConfig)
                    } else {
                        this.showAddPayment = false;
                    }

                } catch(e) {
                }
            }
        },
		resetPaymentChooser() {
			this.addPaymentInfo = null;
			this.showAddPayment = false;
			this.msg = "";
			this.chosenPaymentKey = null;
			this.showAllPayments = true;
		},
		logoutAction() {
			this.resetPaymentChooser();
        },
        isValidIFrameOrigin(event) {
            if (this.location.paymentServiceType === EPaymentServiceType.TYPE_CC) {
                return event.origin.includes(PAYMENT_ENDPOINTS.TYPE_CC.BASE_URL);
            }
            return false;
        },
      captchaSolved(solvedCaptcha) {
          this.solvedCaptcha = solvedCaptcha;
      }
    },
    mounted() {
        const vue = this;
        window.addEventListener('message', (event) => {
            // iframe token for payment processor
            if (event && event.data && vue.isValidIFrameOrigin(event)) {
                const data = JSON.parse(event.data);
                if (data.message && data.message.length && data.errorCode == '0') {
                    vue.token = data.message;
                } else {
                    vue.token = null;
                }
            }
        }, false);

		if (this.hasPaymentAccounts) {
			const customer = this.customer;
			const paymentAccount = customer.getDefaultPaymentAccount();
			this.setPayment(paymentAccount);
        }
		this.showAllPayments = true;
	},
	created() {
        EventBus.$on('logout', this.logoutAction);
	},
	destroyed() {
		EventBus.$off('logout');
	},
}
</script>

<style lang="scss" scoped>
@import 'globalStyles';

.resp-iframe-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  min-height: 16em;
}
</style>



// WEBPACK FOOTER //
// src/components/checkout/PaymentChooser.vue